import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Truck, Package, ShieldCheck, Clock, MapPin, Users, Building, UtensilsCrossed, ShoppingCart, Globe, Box, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CategorySidebar } from '../components/products/CategorySidebar';
import { ProductGrid } from '../components/products/ProductGrid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_IMAGES = {
  'Rice': 'https://images.unsplash.com/photo-1586201375761-83865001e31c?crop=entropy&cs=srgb&fm=jpg&q=85',
  'Ghee': 'https://images.unsplash.com/photo-1631452180519-c014fe946bc7?crop=entropy&cs=srgb&fm=jpg&q=85',
  'Sugar': 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?crop=entropy&cs=srgb&fm=jpg&q=85',
  'Flour': 'https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?crop=entropy&cs=srgb&fm=jpg&q=85',
  'Peanuts': 'https://images.pexels.com/photos/4202964/pexels-photo-4202964.jpeg',
  'Chana': 'https://images.unsplash.com/photo-1705845580041-f04ad853617f?crop=entropy&cs=srgb&fm=jpg&q=85',
  'Gajaks': 'https://images.unsplash.com/photo-1655542049722-395a243e12b7?crop=entropy&cs=srgb&fm=jpg&q=85',
  'Chikki': 'https://images.unsplash.com/photo-1655542049722-395a243e12b7?crop=entropy&cs=srgb&fm=jpg&q=85',
  'Namkeens': 'https://images.unsplash.com/photo-1601050690597-df0568f70950?crop=entropy&cs=srgb&fm=jpg&q=85',
  'default': 'https://images.unsplash.com/photo-1606824722920-4c652a70f348?crop=entropy&cs=srgb&fm=jpg&q=85'
};

const HERO_IMAGE = "https://images.unsplash.com/photo-1768796373360-95d80c5830fb?crop=entropy&cs=srgb&fm=jpg&q=85";
const LOGISTICS_IMAGE = "https://images.unsplash.com/photo-1501700493788-fa1a4fc9fe62?crop=entropy&cs=srgb&fm=jpg&q=85";

// Brand logos for marquee
const BRAND_LOGOS = [
  { name: "Kohinoor", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/u36vwckb_365811d7c25847dc2d35bcb41c28cee8.w2895.h904.png" },
  { name: "Jabsons", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/x225u3jv_images%20%281%29.png" },
  { name: "Bikano", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/1ewowc1n_images.png" },
  { name: "Maniarr's", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/ltgojhhh_572047_663274.webp" },
  { name: "Australian Valley", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/4m2m4cma_AV%20logo.png" },
  { name: "Shakti Bhog", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/b6pejn4y_images%20%282%29.png" },
  { name: "Namo", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/tte6a3pq_R4907031.MEDIUM.JPG" },
  { name: "Sartaj", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/gc20ufum_sartaj-lgoo.png" },
  { name: "Golden Temple", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/3utot1rd_GT_Logo.pdf.png" },
  { name: "Haldiram's", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/3kbsdaxx_Haldiram%27s_2024_Logo.svg.png" },
  { name: "Lal Qilla", url: "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/7qez8do0_images%20%283%29.png" },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

// Trusted Brands Marquee Component
function TrustedBrands() {
  const logos = [...BRAND_LOGOS, ...BRAND_LOGOS, ...BRAND_LOGOS, ...BRAND_LOGOS];
  
  return (
    <section data-testid="trusted-brands-section" className="py-16 bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <h2 className="text-2xl md:text-3xl font-bold text-[#0f172a] text-center">Trusted Brands</h2>
        <p className="text-slate-600 text-center mt-2">Our portfolio reflects longstanding partnerships with respected brands, both past and present.</p>
      </div>
      <div className="relative overflow-hidden">
        <div className="flex animate-marquee">
          {logos.map((brand, i) => (
            <div key={i} className="flex-shrink-0 mx-8 md:mx-12">
              <img src={brand.url} alt={brand.name} className="h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Product Detail Modal Component
function ProductModal({ product, onClose }) {
  if (!product) return null;
  
  return (
    <Dialog open={!!product} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{product.name}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {product.image ? (
            <img src={product.image} alt={product.name} className="w-full h-64 object-cover rounded-lg mb-6" />
          ) : (
            <div className="w-full h-64 bg-slate-100 rounded-lg flex items-center justify-center mb-6">
              <Package className="w-16 h-16 text-slate-300" />
            </div>
          )}
          {product.description && <p className="text-slate-600 mb-6">{product.description}</p>}
          <div className="grid grid-cols-2 gap-4">
            {product.unit_size && (
              <div className="flex items-start gap-3">
                <Box className="w-5 h-5 text-[#01195d] mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Unit Size</p>
                  <p className="text-sm text-slate-600">{product.unit_size}</p>
                </div>
              </div>
            )}
            {product.carton_size && (
              <div className="flex items-start gap-3">
                <Package className="w-5 h-5 text-[#01195d] mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Carton Size</p>
                  <p className="text-sm text-slate-600">{product.carton_size}</p>
                </div>
              </div>
            )}
            {product.country_of_origin && (
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-[#01195d] mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Country of Origin</p>
                  <p className="text-sm text-slate-600">{product.country_of_origin}</p>
                </div>
              </div>
            )}
            {product.ingredients && (
              <div className="flex items-start gap-3 col-span-2">
                <List className="w-5 h-5 text-[#01195d] mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Ingredients</p>
                  <p className="text-sm text-slate-600">{product.ingredients}</p>
                </div>
              </div>
            )}
          </div>
          <div className="mt-6 pt-6 border-t border-slate-100">
            <Button onClick={() => { onClose(); scrollTo('enquiry-form'); }} className="w-full bg-[#01195d] hover:bg-[#01195d]/90 h-12">
              Enquire About This Product
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Category Card Component
// (Removed: replaced by CategorySidebar + ProductGrid layout)

export const HomePage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', business_name: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState({});
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/categories`)
      .then(res => {
        setCategories(res.data);
        if (res.data.length > 0) setSelectedCatId(res.data[0].id);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  const loadCategoryProducts = useCallback(async (catId) => {
    if (products[catId]) return;
    try {
      const res = await axios.get(`${API}/categories/${catId}/products`);
      setProducts(prev => ({ ...prev, [catId]: res.data }));
    } catch (e) { console.error(e); }
  }, [products]);

  useEffect(() => {
    if (selectedCatId) loadCategoryProducts(selectedCatId);
  }, [selectedCatId, loadCategoryProducts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, formData);
      toast.success('Thank you! We will contact you shortly.');
      setFormData({ name: '', email: '', phone: '', business_name: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    { icon: ShieldCheck, title: "Established Wholesaler", desc: "Years of experience supplying quality products to businesses across NSW." },
    { icon: MapPin, title: "Strategic Sydney Location", desc: "Based in Seven Hills, providing efficient access to Greater Sydney." },
    { icon: Truck, title: "Reliable Fulfillment", desc: "Consistent supply chain and timely delivery you can depend on." },
    { icon: Package, title: "Quality Products", desc: "Curated selection of grocery and FMCG products at competitive prices." },
    { icon: Clock, title: "Fast Processing", desc: "Efficient order processing to keep your business running smoothly." },
    { icon: Users, title: "Trusted Partner", desc: "Rated by hundreds of customers as a reliable wholesale supplier." }
  ];

  const serve = [
    { icon: ShoppingCart, title: "Independent Grocers", desc: "Supporting local stores with competitive pricing." },
    { icon: Building, title: "Supermarkets", desc: "Reliable supply for retail chains." },
    { icon: UtensilsCrossed, title: "Restaurants & Cafes", desc: "Quality ingredients for hospitality." },
    { icon: Users, title: "Foodservice Operators", desc: "Bulk solutions for catering." }
  ];

  return (
    <div data-testid="home-page">
      <Toaster position="top-right" richColors />
      <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />

      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center" style={{ backgroundImage: `url(${HERO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-[#01195d]/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">Your Trusted Wholesale Partner in Sydney</h1>
            <p className="text-lg md:text-xl text-slate-200 mb-10 leading-relaxed">GS Pacific delivers reliable supply, competitive pricing, and commercial-scale solutions for retailers and foodservice operators across NSW.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => scrollTo('enquiry-form')} className="bg-white text-[#01195d] hover:bg-slate-100 h-14 px-8 font-semibold">Become a Trade Customer</Button>
              <Button variant="outline" onClick={() => window.location.href = '/contact'} className="border-2 border-white text-white hover:bg-white/10 h-14 px-8 font-semibold bg-transparent">Contact Sales</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section id="why-us" data-testid="why-us-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight mb-4">Why Choose GS Pacific</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">We're committed to being the wholesale partner your business can rely on.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <div key={i} className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-[#01195d]/10 rounded-lg flex items-center justify-center mb-6">
                  <f.icon className="w-7 h-7 text-[#01195d]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0f172a] mb-3">{f.title}</h3>
                <p className="text-slate-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" data-testid="products-section" className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight mb-4">What We Supply</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Quality products at wholesale prices, delivered with consistency.</p>
          </div>
          {isLoading ? (
            <div className="text-center py-12 text-slate-500">Loading categories...</div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p>Product categories coming soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">
              <CategorySidebar
                categories={categories}
                selectedId={selectedCatId}
                onSelect={setSelectedCatId}
              />
              <ProductGrid
                category={categories.find(c => c.id === selectedCatId)}
                products={products[selectedCatId]}
                onProductClick={setSelectedProduct}
              />
            </div>
          )}
        </div>
      </section>

      {/* Who We Serve */}
      <section id="who-we-serve" data-testid="who-we-serve-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight mb-4">Who We Serve</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">From local grocers to large-scale foodservice operators.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {serve.map((s, i) => (
              <div key={i} className="text-center p-8 rounded-xl bg-[#f8fafc] hover:bg-[#01195d] group transition-all duration-300">
                <div className="w-16 h-16 bg-[#01195d]/10 group-hover:bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 transition-colors">
                  <s.icon className="w-8 h-8 text-[#01195d] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f172a] group-hover:text-white mb-2 transition-colors">{s.title}</h3>
                <p className="text-slate-600 text-sm group-hover:text-slate-200 transition-colors">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Brands */}
      <TrustedBrands />

      {/* Location */}
      <section data-testid="location-section" className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight mb-6">Sydney-Based Distribution</h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">Our Seven Hills warehouse provides strategic access to Greater Sydney and surrounding regions.</p>
              <ul className="space-y-4">
                <li className="flex items-center gap-4"><div className="w-10 h-10 bg-[#01195d]/10 rounded-lg flex items-center justify-center"><MapPin className="w-5 h-5 text-[#01195d]" /></div><span className="text-slate-700">Central Sydney warehouse location</span></li>
                <li className="flex items-center gap-4"><div className="w-10 h-10 bg-[#01195d]/10 rounded-lg flex items-center justify-center"><Truck className="w-5 h-5 text-[#01195d]" /></div><span className="text-slate-700">Efficient local and regional distribution</span></li>
                <li className="flex items-center gap-4"><div className="w-10 h-10 bg-[#01195d]/10 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-[#01195d]" /></div><span className="text-slate-700">Fast order processing and dispatch</span></li>
              </ul>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img src={LOGISTICS_IMAGE} alt="GS Pacific Logistics" className="w-full h-80 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section data-testid="trust-section" className="py-16 bg-[#01195d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Trusted by Businesses Across NSW</h3>
              <p className="text-slate-300">Building long-term partnerships through consistent quality and service.</p>
            </div>
            <div className="flex items-center gap-12">
              <div className="text-center"><p className="text-2xl font-bold text-white">100+</p><p className="text-slate-300 text-sm">Trade Customers</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-white">25+</p><p className="text-slate-300 text-sm">Years Experience</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry-form" data-testid="enquiry-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight mb-6">Partner With a Wholesaler You Can Rely On</h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">Ready to streamline your supply chain? Fill out the form and our team will be in touch.</p>
              <ul className="space-y-4">
                {['Competitive wholesale pricing', 'Dedicated account management', 'Flexible ordering and delivery'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <div className="w-6 h-6 bg-[#01195d] rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#f8fafc] p-8 md:p-10 rounded-2xl">
              <h3 className="text-xl font-semibold text-[#0f172a] mb-6">Trade Enquiry Form</h3>
              <form onSubmit={handleSubmit} data-testid="trade-enquiry-form" className="space-y-5">
                <div><Label htmlFor="name">Full Name *</Label><Input id="name" name="name" required value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} className="mt-1.5 h-12" placeholder="Your full name" /></div>
                <div><Label htmlFor="email">Email Address *</Label><Input id="email" name="email" type="email" required value={formData.email} onChange={e => setFormData(p => ({...p, email: e.target.value}))} className="mt-1.5 h-12" placeholder="you@business.com" /></div>
                <div><Label htmlFor="phone">Phone Number *</Label><Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={e => setFormData(p => ({...p, phone: e.target.value}))} className="mt-1.5 h-12" placeholder="04XX XXX XXX" /></div>
                <div><Label htmlFor="business_name">Business Name *</Label><Input id="business_name" name="business_name" required value={formData.business_name} onChange={e => setFormData(p => ({...p, business_name: e.target.value}))} className="mt-1.5 h-12" placeholder="Your business name" /></div>
                <div><Label htmlFor="message">Message (Optional)</Label><Textarea id="message" name="message" rows={4} value={formData.message} onChange={e => setFormData(p => ({...p, message: e.target.value}))} className="mt-1.5" placeholder="Tell us about your business needs..." /></div>
                <Button type="submit" disabled={isSubmitting} className="w-full bg-[#01195d] hover:bg-[#01195d]/90 h-14 font-semibold">{isSubmitting ? 'Submitting...' : 'Submit Enquiry'}</Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
